import { useState, useCallback, useRef, useEffect } from 'react';
import { TIMING } from '../constants/appConstants';

/**
 * useToast Hook
 * Custom hook for managing toast notifications
 * @returns {Object} Object containing toasts array, showToast function, and removeToast function
 */
export const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const timeoutRefs = useRef({});

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(timeoutId => clearTimeout(timeoutId));
      timeoutRefs.current = {};
    };
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    const toast = { id, message, type };
    
    setToasts(prev => [...prev, toast]);
    
    const timeoutId = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete timeoutRefs.current[id];
    }, TIMING.TOAST_DURATION_MS);
    
    timeoutRefs.current[id] = timeoutId;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    // Cleanup timeout if it exists
    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id]);
      delete timeoutRefs.current[id];
    }
  }, []);

  return { toasts, showToast, removeToast };
};

